// Cookie consent + saved preferences hook for BookFreely.
//
// Storage model
// - Consent state lives in localStorage under `bf.consent.v1` — a JSON blob with:
//     { essential: true, functional: bool, analytics: bool, marketing: bool, at: ISO }
// - Feature preferences (recent searches, location, favourites, dashboard prefs,
//   filters) live under separate `bf.pref.<name>` keys, but ONLY when the user
//   has consented to the `functional` category. Reads always work (fallback to
//   default value); writes are silently no-op'd until consent is granted so we
//   never leave stale personal data in storage after a "Reject non-essential".
//
// Every call goes through `readPref` / `writePref` so a future switch to a
// cookie or a server-side store is a one-line change.

const CONSENT_KEY = "bf.consent.v1";
const PREF_PREFIX = "bf.pref.";
const REMEMBER_KEY = "bf.remember_me";

const DEFAULT_CONSENT = {
  essential: true,
  functional: false,
  analytics: false,
  marketing: false,
  at: null,
};

function safeRead(k) {
  try { return typeof window !== "undefined" ? window.localStorage.getItem(k) : null; }
  catch { return null; }
}
function safeWrite(k, v) {
  try { if (typeof window !== "undefined") window.localStorage.setItem(k, v); }
  catch { /* private-mode / quota */ }
}
function safeRemove(k) {
  try { if (typeof window !== "undefined") window.localStorage.removeItem(k); }
  catch { /* noop */ }
}

export function getConsent() {
  const raw = safeRead(CONSENT_KEY);
  if (!raw) return null; // never asked before → show banner
  try {
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_CONSENT, ...parsed, essential: true };
  } catch {
    return null;
  }
}

export function saveConsent(nextPartial) {
  const merged = { ...DEFAULT_CONSENT, ...nextPartial, essential: true, at: new Date().toISOString() };
  safeWrite(CONSENT_KEY, JSON.stringify(merged));
  // If the user just revoked functional consent, purge stored preferences
  if (!merged.functional) purgeFunctionalPrefs();
  window.dispatchEvent(new CustomEvent("bf-consent-change", { detail: merged }));
  return merged;
}

export function acceptAllConsent() {
  return saveConsent({ functional: true, analytics: true, marketing: true });
}

export function rejectNonEssentialConsent() {
  return saveConsent({ functional: false, analytics: false, marketing: false });
}

function purgeFunctionalPrefs() {
  if (typeof window === "undefined") return;
  const remove = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const k = window.localStorage.key(i);
    if (k && k.startsWith(PREF_PREFIX)) remove.push(k);
  }
  remove.forEach(safeRemove);
}

/**
 * Read a stored preference. Safe when there's no consent — returns fallback.
 * @template T
 * @param {string} name
 * @param {T} fallback
 * @returns {T}
 */
export function readPref(name, fallback) {
  const raw = safeRead(PREF_PREFIX + name);
  if (raw == null) return fallback;
  try { return JSON.parse(raw); } catch { return fallback; }
}

/**
 * Write a preference. Silently no-ops if the user hasn't consented to
 * functional cookies (privacy-preserving default).
 */
export function writePref(name, value) {
  const c = getConsent();
  if (!c || !c.functional) return false;
  safeWrite(PREF_PREFIX + name, JSON.stringify(value));
  return true;
}

// ---- Remember-me (not a preference — controls session length) ----
export function setRememberMe(on) {
  if (on) safeWrite(REMEMBER_KEY, "1");
  else safeRemove(REMEMBER_KEY);
}
export function getRememberMe() {
  return safeRead(REMEMBER_KEY) === "1";
}
